import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import {
  flatpickrLocale,
  protectFlatpickrTranslation
} from "../helpers/flatpickr_locale"

export default class FlatpickrController extends Controller {
  static values = {
    type: String
  }

  connect() {
    this.handleLanguageChange = this.languageChange.bind(this)
    this.handleBeforeCache = this.teardown.bind(this)
    document.addEventListener("itoons:language-change", this.handleLanguageChange)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)

    this.removeCachedDisplayInput()

    const lockTimeInputs = (instance) => {
      const timeInputs = [instance.hourElement, instance.minuteElement, instance.secondElement]
        .filter(Boolean)

      for (const input of timeInputs) {
        input.setAttribute("readonly", "readonly")
        input.setAttribute("inputmode", "none")
        input.style.caretColor = "transparent"
      }
    }

    const showConfirmButton = this.typeValue === "datetime"
    let clearButton

    const syncClearButton = (selectedDates) => {
      clearButton?.classList.toggle("d-none", selectedDates.length === 0)
    }

    let config = {
      locale: flatpickrLocale(),
      disableMobile: true,
      allowInput: false,
      appendTo: document.body,

      onOpen: (selectedDates, dateStr, instance) => {
        this.toggleModalFocusTrap(false)
        if (instance.config.enableTime) {
          lockTimeInputs(instance)
        }
        this.startScrollTracking(instance)
        requestAnimationFrame(() => {
          if (this.positionCalendarWithinViewport(instance)) {
            instance.calendarContainer.scrollTop = 0
          }
        })
      },

      onClose: (selectedDates, dateStr, instance) => {
        this.stopScrollTracking()
        this.toggleModalFocusTrap(true)
      },

      onReady: function (selectedDates, dateStr, instance) {
        instance.input.closest("label")?.addEventListener("click", (event) => {
          if (event.target.closest("svg")) {
            instance.open()
          }
        })
        protectFlatpickrTranslation(instance)
        instance._input.classList.add("flatpickr-display-input")
        instance.daysContainer?.addEventListener("click", (event) => {
          if (!event.target.closest(".flatpickr-day")) return

          requestAnimationFrame(() => {
            if (document.activeElement === instance.hourElement) {
              instance.hourElement.blur()
            }
          })
        })

        const calendarClass = instance.input.dataset.flatpickrCalendarClass
        if (calendarClass) {
          instance.calendarContainer.classList.add(calendarClass)
        }
        if (instance.input.closest(".modal") || calendarClass === "custom-date-picker") {
          instance.calendarContainer.classList.add("flatpickr-modal-calendar")
        }

        if (showConfirmButton) {
          const confirmButtonWrapper = document.createElement("div")
          confirmButtonWrapper.className = "flatpickr-confirm-button-wrapper d-flex justify-content-end gap-2 p-2"

          clearButton = document.createElement("button")
          clearButton.type = "button"
          clearButton.className = "btn py-2 px-3 rounded-2 bg-tomato text-black p-font-bold fs-14 flatpickr-clear-button"
          clearButton.textContent = "Clear"
          clearButton.addEventListener("click", () => {
            instance.clear()
            syncClearButton(instance.selectedDates)
          })

          const confirmButton = document.createElement("button")
          confirmButton.type = "button"
          confirmButton.className = "btn py-2 px-3 rounded-2 bg-info text-black p-font-bold fs-14 flatpickr-confirm-button"
          confirmButton.textContent = "Done"
          confirmButton.addEventListener("click", () => instance.close())

          confirmButtonWrapper.append(clearButton, confirmButton)
          instance.calendarContainer.appendChild(confirmButtonWrapper)
          syncClearButton(selectedDates)
        }

        if (!instance.config.enableTime) return

        lockTimeInputs(instance)

        const inputs = [instance.hourElement, instance.minuteElement].filter(Boolean)

        for (const [index, input] of inputs.entries()) {
          input.addEventListener("input", function () {
            input.value = input.value.replaceAll(/\D/g, "").slice(0, 2)
          })

          input.addEventListener("blur", function () {
            if (input.value === "") return

            let max = index === 0 ? 23 : 59
            let val = Number.parseInt(input.value, 10)

            val = Math.min(max, Math.max(0, val))

            input.value = val.toString().padStart(2, "0")
          })
        }
      },

      onValueUpdate: (selectedDates) => syncClearButton(selectedDates)
    }

    if (this.typeValue === "time") {
      Object.assign(config, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        altInput: true,
        altFormat: "H:i",
        minuteIncrement: 1
      })
    } else if (this.typeValue === "customtime") {
      Object.assign(config, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "h:i K",
        time_24hr: false,
        altInput: true,
        altFormat: "h:i K",
        minuteIncrement: 1
      })
    } else if (this.typeValue === "datetime") {
      Object.assign(config, {
        enableTime: true,
        altFormat: "m-d-Y    h:i K",
        dateFormat: "Y-m-d h:i K",
        altInput: true,
        time_24hr: false
      })
    } else {
      Object.assign(config, {
        altFormat: "m-d-Y",
        dateFormat: "Y-m-d",
        altInput: true,
      })
    }

    this.fp = flatpickr(this.element, config)
    this.preventManualDeletion = (event) => {
      if (event.key !== "Backspace" && event.key !== "Delete") return

      event.preventDefault()
      event.stopImmediatePropagation()
    }
    this.fp._input.addEventListener("keydown", this.preventManualDeletion, true)
  }

  startScrollTracking(instance) {
    this.stopScrollTracking()
    this.scrollContainers = []
    let parent = this.element.parentElement

    while (parent) {
      const style = window.getComputedStyle(parent)

      if (
        ["auto", "scroll", "overlay"].includes(style.overflowY) ||
        ["auto", "scroll", "overlay"].includes(style.overflow)
      ) {
        this.scrollContainers.push(parent)
      }

      parent = parent.parentElement
    }

    // Also track window/document scrolling.
    window.addEventListener("scroll", this.handleScroll, true)
    window.addEventListener("resize", this.handleScroll)
    window.visualViewport?.addEventListener("scroll", this.handleScroll)
    window.visualViewport?.addEventListener("resize", this.handleScroll)

    this.scrollContainers.forEach((container) => {
      container.addEventListener("scroll", this.handleScroll, {
        passive: true
      })
    })
  }

  handleScroll = () => {
    if (!this.fp?.isOpen) return

    // Let the scroll position update first, then reposition Flatpickr.
    requestAnimationFrame(() => {
      if (this.fp?.isOpen) {
        if (!this.positionCalendarWithinViewport(this.fp)) {
          this.fp._positionCalendar()
        }
      }
    })
  }

  positionCalendarWithinViewport(instance) {
    const calendar = instance.calendarContainer
    const viewport = window.visualViewport
    const viewportOffsetLeft = viewport?.offsetLeft || 0
    const viewportOffsetTop = viewport?.offsetTop || 0
    const viewportWidth = viewport?.width || window.innerWidth
    const viewportHeight = viewport?.height || window.innerHeight
    const viewportLeft = window.scrollX + viewportOffsetLeft
    const viewportTop = window.scrollY + viewportOffsetTop
    const isCompactViewport = window.matchMedia("(max-width: 767.98px), (max-height: 540px)").matches
    const isModalCalendar = calendar.classList.contains("flatpickr-modal-calendar")

    if (isModalCalendar && isCompactViewport) {
      calendar.classList.add("flatpickr-viewport-positioned")
      calendar.classList.remove("flatpickr-space-positioned")
      calendar.style.setProperty("--flatpickr-modal-left", `${viewportLeft + viewportWidth / 2}px`)
      calendar.style.setProperty("--flatpickr-modal-top", `${viewportTop + viewportHeight / 2}px`)
      return true
    }

    const margin = 8
    const gap = 4
    const positionElement = instance._positionElement || instance._input || instance.input
    const inputRect = positionElement.getBoundingClientRect()
    const calendarWidth = Math.min(calendar.offsetWidth, Math.max(0, viewportWidth - margin * 2))
    const calendarHeight = calendar.scrollHeight
    const availableBelow = viewportOffsetTop + viewportHeight - inputRect.bottom - gap - margin
    const availableAbove = inputRect.top - viewportOffsetTop - gap - margin
    const openBelow = calendarHeight <= availableBelow || availableBelow >= availableAbove
    const availableHeight = Math.max(0, openBelow ? availableBelow : availableAbove)
    const isHeightConstrained = calendarHeight > availableHeight
    const visibleHeight = Math.min(calendarHeight, availableHeight)
    const viewportRight = viewportOffsetLeft + viewportWidth
    const preferredLeft = inputRect.left + (inputRect.width - calendarWidth) / 2
    const left = Math.min(
      Math.max(preferredLeft, viewportOffsetLeft + margin),
      viewportRight - calendarWidth - margin
    )
    const top = openBelow
      ? window.scrollY + inputRect.bottom + gap
      : window.scrollY + inputRect.top - gap - visibleHeight

    calendar.classList.remove("flatpickr-viewport-positioned")
    calendar.classList.add("flatpickr-space-positioned")
    calendar.classList.toggle("flatpickr-height-constrained", isHeightConstrained)
    calendar.style.setProperty("--flatpickr-modal-left", `${window.scrollX + left}px`)
    calendar.style.setProperty("--flatpickr-modal-top", `${top}px`)
    if (isHeightConstrained) {
      calendar.style.setProperty("--flatpickr-modal-max-height", `${visibleHeight}px`)
    } else {
      calendar.style.removeProperty("--flatpickr-modal-max-height")
      calendar.scrollTop = 0
    }

    return true
  }

  stopScrollTracking() {
    window.removeEventListener("scroll", this.handleScroll, true)
    window.removeEventListener("resize", this.handleScroll)
    window.visualViewport?.removeEventListener("scroll", this.handleScroll)
    window.visualViewport?.removeEventListener("resize", this.handleScroll)

    this.scrollContainers?.forEach((container) => {
      container.removeEventListener("scroll", this.handleScroll)
    })

    this.scrollContainers = []
  }

  toggleModalFocusTrap(active) {
    const modalElement = this.element.closest('.modal')
    const modal = modalElement && bootstrap.Modal.getInstance(modalElement)
    const focusTrap = modal?._focustrap

    if (!focusTrap) return

    if (active && modalElement.classList.contains('show')) {
      focusTrap.activate()
    } else {
      focusTrap.deactivate()
    }
  }

  disconnect() {
    document.removeEventListener("itoons:language-change", this.handleLanguageChange)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    this.teardown()
  }

  teardown() {
    this.stopScrollTracking()
    document.body.classList.remove("flatpickr-open")
    this.toggleModalFocusTrap(true)
    this.fp?._input?.removeEventListener("keydown", this.preventManualDeletion, true)
    this.fp?.destroy()
    this.fp = null
  }

  removeCachedDisplayInput() {
    let sibling = this.element.nextElementSibling

    while (sibling?.classList.contains("flatpickr-display-input")) {
      const duplicate = sibling
      sibling = sibling.nextElementSibling
      duplicate.remove()
    }

    this.element.classList.remove("flatpickr-input")
    this.element.removeAttribute("readonly")
    if (this.element.type === "hidden") this.element.type = "text"
  }

  languageChange(event) {
    this.fp?.set("locale", flatpickrLocale(event.detail?.language))
  }

  clear() {
    this.fp?.clear()
  }
}
